/**
 * Melibo
 *
 * Filename: melibo.js
 *
 * @category  Fahrradxxl
 * @package   Module
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2024 Fahrrad-xxl.de
 *
 * Melibo ist ein intuitives und datenschutzkonformes KI-Tool für den Kundensupport.
 * Das hybride Support-Tool vereint Eigenschaften von Chatbot Plattformen und Support-Tools.
 * Technologische Basis des Services sind Künstliche Intelligenz (KI) und Natural Language Processing (NLP).
 *
 * Script URL V1: https://cdn.melibo.de/bundle.js
 * Script URL V2: https://cdn.melibo.de/v2/widget.js
 *
 * widgetElementName V1: melibo-webchat
 * widgetElementName V2: melibo-widget
 */


import FxxlCore  from '@fxxl/core';
import FxxlError from '@fxxl/error';
import {fxxl_config_from_php} from '@fxxl/globals';


export class FxxlMelibo {

    /*
     * Variables
     * ------------------------------------------------------------------------
     */
    // Base
    scriptUrl = 'https://cdn.melibo.de/335527ec-4b43-4d54-bc32-a968eb5161d0/bundle.js';

    // Widget Selctors
    static widgetUsableCheckTimeout = 20000; // 20sec
    static widgetElementName = 'melibo-webchat';
    static widgetElementCSSSelectorHost = ':host';
    static widgetElementCSSSelectorChat = '.widget-wrapper';
    static widgetElementCSSSelectorIcon = '#app>.toggle';
    static cssMediaQueryViewportMobile = `@media only screen and (max-width: ${(980 - 1)}px)`;
    // Can't use window.largViewportSize for 980 here, because it's not available at this point

    // StyleSheet Identifiers
    static get STYLESHEET_IDENTIFIER() {
        return {
            MAIN: 'main',
            ICON_VISIBILTY: 'icon_visibility',
            ICON_TEMP_POSITION: 'icon_transform_translate'
        };
    }

    // Site Specific
    basketNextStepButtonClass = 'element-cart-accordion-1';
    basketNextStepButtonActiveClass = 'fxxl-accordion--active';
    checkout2StepAdressSelector = 'html[data-onepage-container="#step_address"]';


    /*
     * Getter & Setter
     * ------------------------------------------------------------------------
     */
    get chatbotKey() {
        return FxxlCore.getConfig().server?.presence?.variables?.MELIBO_CHATBOT_KEY ?? '';
    }

    get hasBackendInjectionApproval() {
        return FxxlCore.getConfig().server?.presence?.variables?.MELIBO_ACTIVE ?? false;
    }

    get isKioskMode() {
        return FxxlCore.getConfig().server?.is_kiosk_mode ?? false;
    }

    get isConsentCookieOptionalGranted() {
        let result = false;
        if (typeof dCookies !== 'undefined' && dCookies !== null) {
            result = dCookies.includes('fxxl_optional_cookies=true');
        }
        return result || (fxxl_config_from_php?.presence?.variables?.CONSENT_BANNER_CMT_ENABLED ?? false);
    }

    get canBeInjected()
    {
        return this.isConsentCookieOptionalGranted &&
            this.hasBackendInjectionApproval &&
            this.chatbotKey !== '' &&
            !this.isKioskMode;
    }


    /*
     * Constructor
     * ------------------------------------------------------------------------
     */
    constructor() {
        if (this.canBeInjected) {
            this.injectScript();

            // Set Site Specific Styles
            this.setStylesBaseFxxl();
            this.setStylesBasket();
            this.setStylesCheckout2();
            this.setStylesCheckout4();
        }
    }


    /*
     * Private Methods - Inject
     * ------------------------------------------------------------------------
     */

    injectScript() {
        // Build Widget Element
        const widget = document.createElement(FxxlMelibo.widgetElementName);

        widget.setAttribute('api-key', this.chatbotKey);

        // Build Script Element
        const script = document.createElement('script');
        script.src = this.scriptUrl;
        script.onerror = () => new FxxlError('Error: Melibo konnte nicht richtig geladen werden.');
        script.onload = () => {
            document.body.append(widget);
        };

        // Inject Script
        document.head.appendChild(script);
    }


    /*
     * Private Methods - Set Site Specific Styles
     * ------------------------------------------------------------------------
     */
    // Base Fxxl
    setStylesBaseFxxl() {
        // TODO: Keeped for V2 Style Widget Chat
        // FxxlMelibo.setWidgetChatMainStyles({
        //     'z-index': '999 !important'
        // });

        // Style Widget Icon
        if (isLargeViewport()) {
            FxxlMelibo.setWidgetIconMainStyles({
                'z-index': 11,
                transition: 'transform .25s cubic-bezier(.4,0,.2,1)'
            });
        } else {
            FxxlMelibo.setWidgetIconMainStyles({
                'z-index': 10,
                transition: 'transform .25s cubic-bezier(.4,0,.2,1)'
            });
        }
    }

    // Basket
    setStylesBasket() {
        if (site_type === 'warenkorb' && site_name === 'detail') {
            // Mobile: Style Widget Icon
            if (!isLargeViewport()) {
                FxxlMelibo.setWidgetIconMainStyles({
                    bottom: '60px'
                });

                // Observe Mobile NextStep Button & adjust the height of the Widget Icon
                this.setStylesBasketObserveNextStepButton();
            }
        }
    }

    setStylesBasketObserveNextStepButton () {
        const nextStepButton = document.getElementById(`${this.basketNextStepButtonClass}`);

        if (nextStepButton) {
            const nextStepButtonObserver = new MutationObserver(mutations => {
                if (nextStepButton.classList.contains(this.basketNextStepButtonActiveClass)) {
                    FxxlMelibo.setWidgetIconTransformTranslate(0, -170);
                } else {
                    FxxlMelibo.setWidgetIconTransformTranslate(0, 0);
                }
            });

            nextStepButtonObserver.observe(nextStepButton, { attributeFilter: ['class'] });
        }
    }

    // Checkout 2
    setStylesCheckout2() {
        if (site_type === 'warenkorb' && site_name === 'checkout2') {

            // Mobile: Style Widget Icon
            if (!isLargeViewport()) {
                const checkout2StepAdress = document.querySelector(this.checkout2StepAdressSelector);

                // IF Page Loads at Step: Adress -> Position the Icon correctly
                if (checkout2StepAdress) {
                    FxxlMelibo.setWidgetIconTransformTranslate(0, -125);
                }
            }
        }
    }

    // Checkout 4
    setStylesCheckout4() {
        if (site_type === 'warenkorb' && site_name === 'checkout4') {
            // Mobile: Style Widget Icon
            if (!isLargeViewport()) {
                FxxlMelibo.setWidgetIconMainStyles({
                    bottom: '160px'
                });
            }
        }
    }


    /*
     * Static / Public Methods
     * ------------------------------------------------------------------------
     */

    /**
     * This functiom checks if the widget is usable.
     *
     * @returns {Promise} -> Resolves with the DOMElement "melibo-webchat"
     */
    static widgetUsable() {
        return new Promise((resolve, reject) => {
            const widget = document.querySelector(FxxlMelibo.widgetElementName);

            // Melibo injects like 20 stylesheets
            // into the widgets shadowroot, so we can expect that.
            if (widget?.shadowRoot?.styleSheets?.length > 0) {
                return resolve(widget);
            }

            // Set Timeout countdown
            const timeout = setTimeout(() => {
                observer.disconnect();
                return reject('Widget not usable. Timeout reached.');
            }, FxxlMelibo.widgetUsableCheckTimeout);

            // Wait until WWidget is ready
            const observer = new MutationObserver(mutations => {
                const widget = document.querySelector(FxxlMelibo.widgetElementName);

                if (widget?.shadowRoot?.styleSheets?.length > 0) {
                    clearTimeout(timeout);
                    observer.disconnect();
                    return resolve(widget);
                }
            });

            observer.observe(document.body, { childList: true });
        });
    }


    /**
     * This function takes css blocks and parses them to the shadowroot part of the widget.
     * Here we can make css changes for the icon or the chat element that aren't reachable
     * via normal CSS definitions outside the widget shadowroot.
     *
     * Info:
     * - Arg - identifier:  In order to avoid CSS Rule Bloating, Stylesheets get an "identifier" attribute attached,
     *                      onto which we can refer to later on, if a sheet needs to update.
     *                      If certain styles are prone to change, it's highly recommended to use an unique identifier.
     *
     * - Var - styles:      CSS Rules / Blocks get restricted automatically by the current Viewport
     *                      based on `isLargeViewport()`.
     *                      This is has been included to avoid unnecessary CSS Rule Bloating.
     *
     * @param {string} indentifier = FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN
     * @param {string} selector
     * @param {Object} newStyles
     */
    static overwriteWidgetStyles(
        identifier = FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN,
        selector,
        newStyles
    ) {
        if (
            typeof selector !== "string"
            || typeof newStyles !== "object"
            || !selector
            || Object.keys(newStyles).length === 0
        ) {
            return;
        }

        // Prepare Style-String
        // - Combine all property adjustmments
        let styles = Object.entries(newStyles).map(entry => `${entry?.[0]}: ${entry?.[1]};`).join(' ');
        // - Bind property adjustmments to selector
        styles = `${selector} { ${styles} }`;
        // - Bind mobile query to CSS Block
        styles = (!isLargeViewport()) ? `${FxxlMelibo.cssMediaQueryViewportMobile} { ${styles} }` : styles;

        // Wait until Widget is ready
        FxxlMelibo.widgetUsable().then((widget) => {
            let styleSheetUpdated = false;
            const widgetStylesheets = widget?.shadowRoot?.adoptedStyleSheets;

            // Update an existing Stylesheet
            widgetStylesheets.forEach((stylesheet, index) => {
                if (
                    identifier === FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN
                    && stylesheet?.identifier === identifier
                ) {

                    // Update Main StyleSheet
                    const oldRules = stylesheet?.cssRules;
                    const oldStyles = Object.entries(oldRules).map(entry => entry?.[1]?.cssText).join(' ');
                    const newMainStyles = `${oldStyles} ${styles}`;

                    widgetStylesheets[index].replaceSync(newMainStyles);
                    styleSheetUpdated = true;
                    return;
                } else if (
                    identifier !== FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN
                    && stylesheet?.identifier === identifier
                ) {

                    // Update different StyleSheet
                    widgetStylesheets[index]?.replaceSync(styles);
                    styleSheetUpdated = true;
                    return;
                }
            });

            // Push new Stylesheet
            if (!styleSheetUpdated) {
                const sheet = new CSSStyleSheet();
                sheet.identifier = identifier;
                sheet.replaceSync(styles);

                widgetStylesheets.push(sheet);
            }
        }).catch(function (error) {
            new FxxlError(`Melibo: ${error}`);
        });
    }

    /**
     * Wrapper for setting base styles for the widget chat window
     *
     * @param {object} styles
     */
    static setWidgetChatMainStyles(styles) {
        if (typeof styles !== "object" || Object.keys(styles).length === 0) {
            return;
        }

        // Style Widget Icon
        FxxlMelibo.overwriteWidgetStyles(
            FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN,
            FxxlMelibo.widgetElementCSSSelectorChat,
            styles
        );
    }

    /**
     * Wrapper for setting base styles for the widget icon
     *
     * @param {object} styles
     */
    static setWidgetIconMainStyles(styles) {
        if (typeof styles !== "object" || Object.keys(styles).length === 0) {
            return;
        }

        // Style Widget Icon
        FxxlMelibo.overwriteWidgetStyles(
            FxxlMelibo.STYLESHEET_IDENTIFIER.MAIN,
            FxxlMelibo.widgetElementCSSSelectorIcon,
            styles
        );
    }

    /**
     * Sets the CSS transform translate(x, y) Property of the widget icon
     *
     * @param {number} x
     * @param {number} y
     */
    static setWidgetIconTransformTranslate(x, y) {
        if (typeof x !== "number" || typeof y !== "number") {
            return;
        }

        // Mobile: Style Widget Icon
        FxxlMelibo.overwriteWidgetStyles(
            FxxlMelibo.STYLESHEET_IDENTIFIER.ICON_TEMP_POSITION,
            FxxlMelibo.widgetElementCSSSelectorIcon, {
                transform: `translate(${x}px, ${y}px)`
            }
        );
    }

    /**
     * Hides the widget icon
     */
    static hideWidgetIcon() {
        FxxlMelibo.overwriteWidgetStyles(
            FxxlMelibo.STYLESHEET_IDENTIFIER.ICON_VISIBILTY,
            FxxlMelibo.widgetElementCSSSelectorIcon, {
                display: 'none !important'
            }
        );
    }

    /**
     * Show the widget icon
     */
    static showWidgetIcon() {
        FxxlMelibo.overwriteWidgetStyles(
            FxxlMelibo.STYLESHEET_IDENTIFIER.ICON_VISIBILTY,
            FxxlMelibo.widgetElementCSSSelectorIcon, {
                display: 'flex !important'
            }
        );
    }
}